import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import api from '../api/api';
import './Login.css';

function Login() {
  const { user, login, logout } = useUser();
  const navigate = useNavigate();

  // ── Logged-in view ──
  if (user) {
    return (
      <div className="login-page section">
        <div className="login-box">
          <div className="login-avatar">👤</div>
          <h2>Hello, {user.name}!</h2>
          <p className="login-sub">{user.mobile || user.email}</p>
          <Link to="/orders" className="btn login-btn">📦 My Orders</Link>
          <button
            className="btn login-btn login-btn-outline"
            onClick={() => { logout(); navigate('/'); }}
          >
            🚪 Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page section">
      <div className="login-box">
        <h2>Sign In / Sign Up</h2>
        
        <LoginForm onLogin={(u) => { login(u); navigate('/orders'); }} />
      </div>
    </div>
  );
}

function LoginForm({ onLogin }) {
  const [subStep, setSubStep] = useState('contact');
  const [contactType, setContactType] = useState('mobile');
  const [loginMode, setLoginMode] = useState('both');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [name, setName] = useState('');
  const [isNew, setIsNew] = useState(false);
  const [devOtp, setDevOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const otpRefs = Array.from({ length: 4 }, () => React.createRef());

  // Load login mode setting
  React.useEffect(() => {
    api.get('/auth-settings').then(({ data }) => {
      const mode = data?.loginMode || 'both';
      setLoginMode(mode);
      if (mode === 'email_only') setContactType('email');
      else if (mode === 'sms_only') setContactType('mobile');
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = contactType === 'mobile' ? { mobile } : { email };
      const { data } = await api.post('/user/send-otp', payload);
      setIsNew(data.isNew);
      setDevOtp(data.devOtp || '');
      setSubStep('otp');
      setResendTimer(60);
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send code. Please try again.');
    }
    setLoading(false);
  };

  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    if (val && i < 3) otpRefs[i + 1].current?.focus();
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs[i - 1].current?.focus();
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) { setOtp(pasted.split('')); otpRefs[3].current?.focus(); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 4) { setError('Please enter the full 4-digit code.'); return; }
    setError('');
    setLoading(true);
    try {
      if (isNew && !name) { setSubStep('name'); setLoading(false); return; }
      const payload = contactType === 'mobile'
        ? { mobile, otp: code, name: isNew ? name : undefined }
        : { email, otp: code, name: isNew ? name : undefined };
      const { data } = await api.post('/user/verify-otp', payload);
      onLogin(data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    }
    setLoading(false);
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please enter your name.'); return; }
    setError('');
    setLoading(true);
    try {
      const code = otp.join('');
      const payload = contactType === 'mobile' ? { mobile, otp: code, name } : { email, otp: code, name };
      const { data } = await api.post('/user/verify-otp', payload);
      onLogin(data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    }
    setLoading(false);
  };

  // ── Contact step ──
  if (subStep === 'contact') return (
    <form onSubmit={handleSendOtp} className="login-form">
      <p className="login-sub">
        {loginMode === 'email_only' && "Enter your email to receive a one-time code."}
        {loginMode === 'sms_only' && "Enter your mobile number to receive a one-time code."}
        {loginMode === 'both' && "Enter your mobile or email to receive a one-time code."}
      </p>
      {loginMode === 'both' && (
        <div className="login-toggle">
          <button type="button" className={contactType === 'mobile' ? 'active' : ''} onClick={() => setContactType('mobile')}>📱 Mobile</button>
          <button type="button" className={contactType === 'email' ? 'active' : ''} onClick={() => setContactType('email')}>✉️ Email</button>
        </div>
      )}
      {contactType === 'mobile'
        ? <input type="tel" placeholder="087 123 4567" value={mobile} onChange={e => setMobile(e.target.value)} autoFocus required />
        : <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} autoFocus required />
      }
      {error && <p className="login-error">{error}</p>}
      <button type="submit" className="btn login-btn" disabled={loading}>{loading ? 'Sending...' : 'Send Login Code'}</button>
    </form>
  );

  // ── OTP step ──
  if (subStep === 'otp') return (
    <form onSubmit={handleVerifyOtp} className="login-form">
      <p className="login-sub">
        Code sent to <strong>{contactType === 'mobile' ? mobile : email}</strong>.{' '}
        <button type="button" className="login-change-btn" onClick={() => { setSubStep('contact'); setOtp(['','','','']); setError(''); }}>Change</button>
      </p>
      {devOtp && (
        <div className="login-dev-otp">Dev OTP: <strong>{devOtp}</strong></div>
      )}
      <div className="login-otp-row" onPaste={handleOtpPaste}>
        {otp.map((digit, i) => (
          <input
            key={i}
            ref={otpRefs[i]}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={e => handleOtpChange(i, e.target.value)}
            onKeyDown={e => handleOtpKeyDown(i, e)}
            className="login-otp-box"
            autoFocus={i === 0}
          />
        ))}
      </div>
      {error && <p className="login-error">{error}</p>}
      <button type="submit" className="btn login-btn" disabled={loading || otp.join('').length < 4}>{loading ? 'Verifying...' : 'Verify Code'}</button>
      <div className="login-resend">
        {resendTimer > 0
          ? <span>Resend in {resendTimer}s</span>
          : <button type="button" className="login-change-btn" onClick={async () => {
              setError(''); setOtp(['','','','']); setLoading(true);
              try {
                const payload = contactType === 'mobile' ? { mobile } : { email };
                const { data } = await api.post('/user/send-otp', payload);
                setDevOtp(data.devOtp || ''); setResendTimer(60);
                setTimeout(() => otpRefs[0].current?.focus(), 100);
              } catch { setError('Failed to resend.'); }
              setLoading(false);
            }}>Resend Code</button>
        }
      </div>
    </form>
  );

  // ── Name step (new users) ──
  return (
    <form onSubmit={handleNameSubmit} className="login-form">
      <p className="login-sub">Welcome! What's your name?</p>
      <input placeholder="Your full name" value={name} onChange={e => setName(e.target.value)} autoFocus required />
      {error && <p className="login-error">{error}</p>}
      <button type="submit" className="btn login-btn" disabled={loading}>{loading ? 'Please wait...' : 'Continue'}</button>
    </form>
  );
}

export default Login;
